import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Tooltip,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { useUsers } from "../../hooks/useUsers";
import {
  CreateUserModal,
  EditUserModal,
  DeleteUserModal,
} from "./modals";

const ROLE_COLORS = {
  admin: "primary",
  user: "neutral",
  guest: "warning",
  agent: "success",
};

const ROLES = [
  { value: "", label: "All Roles" },
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
  { value: "guest", label: "Guest" },
  { value: "agent", label: "Agent" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const UserManagementTab = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const {
    users,
    loading,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    setUserPassword,
    toggleUserActive,
  } = useUsers();

  // Filter states
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Pagination states
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const searchLower = search.toLowerCase();
      const matchesSearch =
        !search ||
        user.username?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower);

      const matchesRole = !roleFilter || user.role === roleFilter;

      const matchesStatus =
        !statusFilter ||
        (statusFilter === "true" ? user.is_active : !user.is_active);

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, search, roleFilter, statusFilter]);

  // Paginate users
  const paginatedUsers = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredUsers.slice(start, start + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  // Handlers
  const handleOpenEdit = (user) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleOpenDelete = (user) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  const handleCloseModals = () => {
    setSelectedUser(null);
    setEditModalOpen(false);
    setDeleteModalOpen(false);
  };

  const handleChangeRowsPerPage = (newValue) => {
    const updatedValue = Number(newValue);
    if (updatedValue > 0) {
      setRowsPerPage(updatedValue);
      setPage(0);
    }
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleRefresh = () => {
    fetchUsers();
  };

  const handleToggleActive = async (user) => {
    await toggleUserActive(user.id, !user.is_active);
  };

  const labelDisplayedRows = ({ from, to, count }) =>
    `${from}-${to} of ${count}`;

  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Typography level="title-lg" fontWeight="bold" mb={2}>
        User Management
      </Typography>

      {/* Toolbar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ flex: 1 }}
        >
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            startDecorator={<SearchIcon />}
            sx={{ minWidth: 200 }}
          />

          <Select
            value={roleFilter}
            onChange={(_, value) => {
              setRoleFilter(value);
              setPage(0);
            }}
            sx={{ minWidth: 130 }}
          >
            {ROLES.map((role) => (
              <Option key={role.value} value={role.value}>
                {role.label}
              </Option>
            ))}
          </Select>

          <Select
            value={statusFilter}
            onChange={(_, value) => {
              setStatusFilter(value);
              setPage(0);
            }}
            sx={{ minWidth: 130 }}
          >
            {STATUS_OPTIONS.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>

          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} variant="outlined">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Button
          variant="soft"
          startDecorator={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
        >
          Add User
        </Button>
      </Stack>

      {/* Users Table */}
      {filteredUsers.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            bgcolor: "background.level1",
            borderRadius: 2,
          }}
        >
          <Typography level="body-lg" color="neutral" sx={{ mb: 1 }}>
            {users.length === 0 ? "No users found" : "No users match your filters"}
          </Typography>
          <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
            {users.length === 0
              ? "Get started by adding your first user."
              : "Try adjusting your search or filter criteria."}
          </Typography>
          {users.length === 0 && (
            <Button
              variant="soft"
              startDecorator={<AddIcon />}
              onClick={() => setCreateModalOpen(true)}
            >
              Add Your First User
            </Button>
          )}
        </Box>
      ) : (
        <Sheet
          variant="outlined"
          sx={{
            width: "100%",
            borderRadius: "sm",
            overflow: "auto",
          }}
        >
          <Table
            aria-label="users table"
            stickyHeader
            hoverRow
            sx={{
              "--TableCell-headBackground": "var(--joy-palette-background-level1)",
              "--Table-headerUnderlineThickness": "1px",
              "--TableRow-hoverBackground": "var(--joy-palette-background-level1)",
              "--TableCell-paddingY": "8px",
              "--TableCell-paddingX": "12px",
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 60 }}>ID</th>
                <th style={{ width: 140 }}>Username</th>
                <th style={{ width: 180 }}>Name</th>
                <th style={{ width: 200 }}>Email</th>
                <th style={{ width: 100 }}>Role</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 120 }}>Joined</th>
                <th style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <Typography level="body-sm">{user.id}</Typography>
                  </td>
                  <td>
                    <Typography level="body-sm" fontWeight="md">
                      {user.username}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-sm">
                      {user.first_name || user.last_name
                        ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                        : "-"}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-sm" sx={{ wordBreak: "break-all" }}>
                      {user.email || "-"}
                    </Typography>
                  </td>
                  <td>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={ROLE_COLORS[user.role] || "neutral"}
                    >
                      {user.role || "user"}
                    </Chip>
                  </td>
                  <td>
                    <Tooltip title={user.is_active ? "Click to deactivate" : "Click to activate"}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color={user.is_active ? "success" : "neutral"}
                        onClick={() => handleToggleActive(user)}
                      >
                        {user.is_active ? (
                          <CheckCircleIcon />
                        ) : (
                          <CancelIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {formatDate(user.date_joined)}
                    </Typography>
                  </td>
                  <td>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Edit user">
                        <IconButton
                          size="sm"
                          variant="plain"
                          onClick={() => handleOpenEdit(user)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete user">
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleOpenDelete(user)}
                          disabled={user.id === currentUser?.id}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={8}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "flex-end",
                      py: 1,
                    }}
                  >
                    <FormControl orientation="horizontal" size="sm">
                      <FormLabel>Rows per page:</FormLabel>
                      <Select
                        value={rowsPerPage}
                        onChange={(_, newValue) => handleChangeRowsPerPage(newValue)}
                        size="sm"
                      >
                        <Option value={5}>5</Option>
                        <Option value={10}>10</Option>
                        <Option value={25}>25</Option>
                        <Option value={50}>50</Option>
                      </Select>
                    </FormControl>
                    <Typography sx={{ textAlign: "center", minWidth: 80 }}>
                      {labelDisplayedRows({
                        from: filteredUsers.length === 0 ? 0 : page * rowsPerPage + 1,
                        to: Math.min(filteredUsers.length, (page + 1) * rowsPerPage),
                        count: filteredUsers.length,
                      })}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        disabled={page === 0}
                        onClick={() => handleChangePage(page - 1)}
                      >
                        <KeyboardArrowLeftIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        disabled={page >= totalPages - 1}
                        onClick={() => handleChangePage(page + 1)}
                      >
                        <KeyboardArrowRightIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </td>
              </tr>
            </tfoot>
          </Table>
        </Sheet>
      )}

      {/* Modals */}
      <CreateUserModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreateUser={createUser}
      />

      <EditUserModal
        open={editModalOpen}
        onClose={handleCloseModals}
        user={selectedUser}
        onUpdateUser={updateUser}
        onSetPassword={setUserPassword}
      />

      <DeleteUserModal
        open={deleteModalOpen}
        onClose={handleCloseModals}
        user={selectedUser}
        onConfirmDelete={deleteUser}
        currentUserId={currentUser?.id}
      />
    </>
  );
};

export default UserManagementTab;
